<template>
  <div class="bvp">
    <!-- « Tout est OK » ne se coche pas à la main : il se DÉDUIT des sections
         confirmées et des réponses obligatoires données. Le montrer comme une
         case qu'on ne peut pas cocher — ce que faisait la v1 — donne un
         contrôle mort à cliquer ; ici l'état se lit, il ne se manipule pas. -->
    <InlineNotice
      v-if="allDone"
      variant="success"
      icon="fas fa-check-circle"
      size="md"
      data-testid="vigilance-ok"
    >
      L’analyse du besoin est complète : le devis peut être édité.
    </InlineNotice>

    <template v-else>
      <div v-if="unconfirmedSections.length" class="bvp-block" data-testid="vigilance-missing">
        <h4 class="bvp-block-title">
          <i class="fas fa-list-check" aria-hidden="true"></i>
          {{ sectionsTitle }}
        </h4>
        <ul class="bvp-list">
          <li v-for="(section, i) in unconfirmedSections" :key="`s-${i}`">
            <button type="button" class="bvp-jump" @click="$emit('go', section)">{{ section }}</button>
          </li>
        </ul>
      </div>

      <!-- Plus de liste des réponses obligatoires manquantes (retour Figma :
           « ça fait doublon »). Elle ne l'est que depuis qu'une section ne se
           confirme plus incomplète : une réponse qui manque garde sa section
           dans la liste ci-dessus, et la section la montre en rouge. -->
    </template>

    <!-- Ce qui pèse sur le prix et les formateurs proches : rendus par le
         tunnel, qui connaît la demande — le bilan, lui, n'en sait rien. -->
    <slot name="devis" />

    <DetailItem label="Commentaire interne (visible par l’équipe uniquement)" :copyable="false">
      <Textarea
        :model-value="comment"
        :rows="3"
        :disabled="disabled"
        placeholder="Ajoutez tout point de vigilance ou remarque importante"
        @update:model-value="$emit('update:comment', $event)"
      />
    </DetailItem>
  </div>
</template>

<script setup>
/**
 * POINT DE VIGILANCE — le bilan de l'analyse.
 *
 * Il ne se remplit pas : il se calcule. Ce qui reste à faire y est listé, et
 * chaque section restante s'y clique pour y aller directement — la v1 donnait
 * la liste, mais il fallait ensuite retrouver la section à la main, douze
 * écrans plus haut.
 *
 * PRÉSENTATIONNEL : des props entrent, `go` et `update:comment` sortent.
 */
import { computed } from 'vue'
import { InlineNotice, Textarea } from '../ui'
import { DetailItem } from '../data'

const props = defineProps({
  /** Libellés des sections encore à confirmer. */
  unconfirmedSections: {
    type: Array,
    default: () => []
  },
  /**
   * Réponses obligatoires encore attendues. Elles ne s'affichent plus ici,
   * mais décident encore de « l'analyse est complète » : sur un devis signé,
   * rien ne décoche une section relue incomplète.
   */
  missingFields: {
    type: Array,
    default: () => []
  },
  comment: {
    type: String,
    default: ''
  },
  /** Devis signé : le commentaire ne se modifie plus. */
  disabled: {
    type: Boolean,
    default: false
  },
  /** Devis signé : on constate sans donner d'instruction impossible à suivre. */
  signed: {
    type: Boolean,
    default: false
  }
})

defineEmits(['go', 'update:comment'])

const allDone = computed(() => !props.unconfirmedSections.length && !props.missingFields.length)

const sectionsTitle = computed(() => {
  if (props.signed) return 'Sections non confirmées lors de l’analyse'
  const n = props.unconfirmedSections.length
  return n > 1 ? `${n} sections restent à confirmer` : 'Une section reste à confirmer'
})

</script>

<style scoped>
.bvp {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
}

/* Le ton d'attente du kit (`InlineNotice variant="warning"`) ne convient pas à
   un bloc qui porte une LISTE cliquable : son icône et son texte sont pensés
   pour une phrase. Le bloc reprend ses couleurs, pas sa mise en page. */
.bvp-block {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
  padding: var(--spacing-3) var(--spacing-4);
  border: 1px solid var(--color-warning-lighter, #fdeeba);
  border-radius: var(--radius-md);
  background: var(--color-warning-lightest);
  color: #8a5a00;
}

.bvp-block-title {
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  margin: 0;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
}

.bvp-list {
  margin: 0;
  padding-left: var(--spacing-lg);
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: var(--font-size-sm);
  line-height: 1.5;
}

.bvp-jump {
  padding: 0;
  border: none;
  background: none;
  font-family: inherit;
  font-size: inherit;
  color: inherit;
  text-align: left;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
}

.bvp-jump:hover {
  color: var(--color-text-primary);
}

.bvp-jump:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring);
  border-radius: var(--radius-sm, 4px);
}
</style>
